/**
 * Scheme resolution and persistence for the admin chrome.
 *
 * Modelled on `src/i18n/index.ts`: one storage key, a detect function that
 * prefers the stored choice and falls back to the platform, and a setter that
 * both persists and applies. Reads are wrapped because `localStorage` throws
 * outright in a few browser configurations (Safari private mode, third-party
 * frames with cookies blocked) rather than returning null.
 *
 * ---------------------------------------------------------------------------
 * THIS LOGIC IS DUPLICATED, ON PURPOSE, IN apps/web/index.html.
 * ---------------------------------------------------------------------------
 * The scheme has to be on <html> before first paint, which means before this
 * module is fetched, so the same precedence is spelled out again in a blocking
 * inline script in <head>. The two must agree: same STORAGE_KEY, same accepted
 * values, same SWEEP_COMPLETE guard. Change one, change the other — the inline
 * copy carries a comment pointing back here.
 */

/** A scheme is something the page can actually render. */
export type Scheme = 'light' | 'dark';
/** A preference may also defer to the OS. */
export type ThemePref = 'system' | Scheme;

const STORAGE_KEY = 'libretto42:theme';
const DARK_CLASS = 'app-dark';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Light mode is not shippable until every component reads role tokens: a
 * single leftover cream literal is cream text on a white card. Until then the app
 * resolves to dark no matter what is stored or what the OS asks for, and the
 * theme switcher is not mounted.
 *
 * Flip to true in the final PR, once scripts/count-theme-literals.sh reports 0.
 *
 * Annotated `: boolean` deliberately — without it TypeScript narrows the const
 * to the literal `false` and reports the real resolution path below as dead
 * code, which would make the flip a multi-file edit.
 */
export const SWEEP_COMPLETE: boolean = true;

function readStored(): ThemePref | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* storage unavailable — treat as "nothing stored" */
  }
  return null;
}

function prefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(DARK_QUERY).matches
  );
}

/** The stored preference, defaulting to following the OS. */
export function getPref(): ThemePref {
  return readStored() ?? 'system';
}

/** Persist a preference and apply it immediately. */
export function setPref(p: ThemePref): void {
  try {
    localStorage.setItem(STORAGE_KEY, p);
  } catch {
    /* storage unavailable — the choice still applies for this session */
  }
  applyTheme();
}

/**
 * Which scheme should render right now.
 *
 * Precedence: sweep guard → stored 'light'/'dark' → OS preference → light.
 * A stored 'system' (or nothing stored) falls through to the media query.
 */
export function resolveTheme(): Scheme {
  if (!SWEEP_COMPLETE) return 'dark';
  const stored = readStored();
  if (stored === 'light' || stored === 'dark') return stored;
  return prefersDark() ? 'dark' : 'light';
}

/**
 * Keep <meta name="color-scheme"> honest so the UA styles form controls,
 * scrollbars and the overscroll gutter in the scheme we are actually painting.
 */
function syncColorSchemeMeta(scheme: Scheme): void {
  let meta = document.head.querySelector<HTMLMetaElement>('meta[name="color-scheme"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'color-scheme';
    document.head.appendChild(meta);
  }
  meta.content = scheme;
}

let systemWatcher: MediaQueryList | null = null;

/**
 * Track the OS live, so a 'system' preference follows a sunset/sunrise switch
 * without a reload. Installed once; re-entrant because applyTheme() is what
 * the listener calls.
 */
function watchSystemScheme(): void {
  if (systemWatcher !== null) return;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  systemWatcher = window.matchMedia(DARK_QUERY);
  systemWatcher.addEventListener('change', () => {
    if (getPref() === 'system') applyTheme();
  });
}

/**
 * Put the resolved scheme on <html>. `.app-dark` is the one switch: style.css
 * hangs the dark role tokens off it and PrimeVue's darkModeSelector is the same
 * class, so component tokens follow without a second mechanism.
 *
 * Idempotent — the inline script in index.html has usually already done this,
 * and calling it again just re-affirms the same class.
 */
export function applyTheme(): Scheme {
  const scheme = resolveTheme();
  document.documentElement.classList.toggle(DARK_CLASS, scheme === 'dark');
  syncColorSchemeMeta(scheme);
  watchSystemScheme();
  return scheme;
}
